import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AGE_OPTIONS = [
  { value: '', label: 'Selecteer leeftijd' },
  { value: '0-2 jaar', label: '0-2 jaar' },
  { value: '3 jaar', label: '3 jaar' },
  { value: '4 jaar', label: '4 jaar' },
  { value: '5 jaar', label: '5 jaar' },
  { value: '6 jaar', label: '6 jaar' },
  { value: '7 jaar', label: '7 jaar' },
  { value: '8 jaar', label: '8 jaar' },
  { value: '9 jaar', label: '9 jaar' },
  { value: '10 jaar', label: '10 jaar' },
  { value: '11 jaar', label: '11 jaar' },
  { value: '12 jaar', label: '12 jaar' },
  { value: '13 jaar', label: '13 jaar' },
  { value: '14 jaar', label: '14 jaar' },
  { value: '15 jaar', label: '15 jaar' },
  { value: '16 jaar', label: '16 jaar' },
  { value: '17 jaar', label: '17 jaar' },
];

const Nieuwjaarsreceptie = () => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    street_address: '',
    postal_code: '',
    city: '',
    num_adults: 2,
    num_children: 0,
    children_ages: [],
    dietary_requirements: '',
    comments: '',
    privacy_agreed: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleNumChildrenChange = (e) => {
    const numChildren = parseInt(e.target.value);
    setFormData(prev => ({
      ...prev,
      num_children: numChildren,
      children_ages: Array(numChildren).fill('')
    }));
  };

  const handleChildAgeChange = (index, value) => {
    setFormData(prev => {
      const newAges = [...prev.children_ages];
      newAges[index] = value;
      return { ...prev, children_ages: newAges };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.privacy_agreed) {
      toast.error('Je moet akkoord gaan met de privacyverklaring');
      return;
    }

    // Validate children ages if children > 0
    if (formData.num_children > 0 && formData.children_ages.some(age => !age)) {
      toast.error('Vul alle leeftijden van de kinderen in');
      return;
    }

    setIsSubmitting(true);

    try {
      await axios.post(`${API}/nieuwjaarsreceptie/register`, {
        ...formData,
        registration_type: 'online'
      });
      
      setShowSuccess(true);
      toast.success('🎉 Je inschrijving is succesvol! Check je email voor bevestiging.');
      
      // Reset form
      setFormData({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        street_address: '',
        postal_code: '',
        city: '',
        num_adults: 2,
        num_children: 0,
        children_ages: [],
        dietary_requirements: '',
        comments: '',
        privacy_agreed: false,
      });

      // Scroll to top to show success message
      window.scrollTo({ top: 0, behavior: 'smooth' });
      
    } catch (error) {
      console.error('Registration error:', error);
      toast.error(error.response?.data?.detail || 'Er ging iets mis. Probeer het opnieuw.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToForm = () => {
    document.getElementById('registration-form').scrollIntoView({ behavior: 'smooth' });
  };

  if (showSuccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-yellow-50 to-white">
        {/* Navigation */}
        <nav className="fixed top-0 w-full bg-[#0a56c2] shadow-lg z-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-20">
              <a href="/" className="flex items-center gap-3">
                <img 
                  src={process.env.REACT_APP_DEFAULT_LOGO_URL}
                  alt="Logo" 
                  className="h-16 w-auto sm:h-20"
                />
              </a>
              <a href="/" className="text-white hover:text-[#ffe980] font-medium transition-colors">
                ← Terug naar home
              </a>
            </div>
          </div>
        </nav>

        {/* Success Message */}
        <div className="pt-32 pb-20 px-4">
          <div className="max-w-2xl mx-auto">
            <div className="bg-white rounded-2xl shadow-2xl p-12 text-center">
              <div className="inline-block bg-green-100 p-6 rounded-full mb-6">
                <svg className="w-16 h-16 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              
              <h1 className="text-4xl font-bold text-gray-900 mb-4">Inschrijving Succesvol!</h1>
              
              <p className="text-xl text-gray-600 mb-8">
                Bedankt voor je inschrijving voor de nieuwjaarsreceptie!
              </p>

              <div className="bg-blue-50 p-6 rounded-xl mb-8 border-l-4 border-blue-600">
                <h3 className="font-bold text-gray-900 mb-3 flex items-center justify-center gap-2">
                  <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  Check je email!
                </h3>
                <p className="text-gray-700">
                  Je ontvangt een bevestigingsmail met alle details van je inschrijving.
                  Neem deze mail mee op 18 januari voor snelle toegang.
                </p>
              </div>

              <div className="space-y-4">
                <Button 
                  onClick={() => setShowSuccess(false)}
                  className="bg-blue-600 hover:bg-blue-700 text-white font-bold px-8 py-4 rounded-xl"
                >
                  Nog een inschrijving doen
                </Button>
                
                <div>
                  <a 
                    href="/" 
                    className="text-blue-600 hover:text-blue-800 font-medium"
                  >
                    ← Terug naar homepage
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-yellow-50 to-white">
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-[#0a56c2] shadow-lg z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <a href="/" className="flex items-center gap-3">
              <img 
                src={process.env.REACT_APP_DEFAULT_LOGO_URL}
                alt="Logo" 
                className="h-16 w-auto sm:h-20"
              />
            </a>
            <a href="/" className="text-white hover:text-[#ffe980] font-medium transition-colors">
              ← Terug naar home
            </a>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-16 px-4 relative overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-block mb-4">
                <span className="bg-[#ffe980] text-gray-900 px-4 py-2 rounded-full text-sm font-bold uppercase">
                  Zondag 18 januari 2026 • 10:30
                </span>
              </div>
              
              <h1 className="text-5xl sm:text-6xl font-extrabold text-gray-900 mb-6">
                🎉 Nieuwjaarsreceptie 2026
              </h1>
              
              <p className="text-xl text-gray-700 mb-8">
                Het Feestcomité Leupegem nodigt je uit om samen te klinken op het nieuwe jaar! 
                Schrijf je in en vier samen met ons de start van 2026.
              </p>

              <div className="space-y-4 mb-8">
                <div className="flex items-center gap-3 text-gray-700">
                  <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <span className="font-medium">Zondag 18 januari 2026 om 10:30</span>
                </div>
                
                <div className="flex items-center gap-3 text-gray-700">
                  <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span className="font-medium">Tuin Immo Nobels, Leupegem</span>
                </div>

                <div className="flex items-center gap-3 text-gray-700">
                  <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
                  </svg>
                  <span className="font-medium">Gratis toegang voor alle inwoners</span>
                </div>
              </div>

              <Button 
                onClick={scrollToForm}
                className="bg-blue-600 hover:bg-blue-700 text-white font-bold px-8 py-4 rounded-xl text-lg shadow-lg hover:scale-105 transition-all"
              >
                Schrijf je nu in!
              </Button>
            </div>

            <div className="relative">
              <img 
                src="https://res.cloudinary.com/df3x2ly2n/image/upload/v1762329713/feestcomite/migrated/z0bwrfbwgh2sj82yp9lt.png"
                alt="Nieuwjaarsreceptie" 
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Registration Form */}
      <section id="registration-form" className="py-16 px-4">
        <div className="max-w-3xl mx-auto">
          <div className="bg-white rounded-2xl shadow-2xl p-8 md:p-12">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-2">Inschrijvingsformulier</h2>
              <p className="text-gray-600">Vul onderstaande gegevens in om je aan te melden</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Step 1: Personal Info */}
              <div className="bg-blue-50 p-6 rounded-xl">
                <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <span className="bg-blue-600 text-white w-8 h-8 rounded-full flex items-center justify-center text-sm">1</span>
                  Jouw gegevens
                </h3>
                
                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Voornaam *</label>
                    <Input
                      type="text"
                      name="first_name"
                      value={formData.first_name}
                      onChange={handleInputChange}
                      required
                      placeholder="Jan"
                      className="w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Achternaam *</label>
                    <Input
                      type="text"
                      name="last_name"
                      value={formData.last_name}
                      onChange={handleInputChange}
                      required
                      placeholder="Janssens"
                      className="w-full"
                    />
                  </div>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    placeholder="jan.janssens@example.be"
                    className="w-full"
                  />
                  <p className="text-xs text-gray-500 mt-1">Je ontvangt een bevestigingsmail</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Telefoonnummer (optioneel)</label>
                  <Input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="0478 12 34 56"
                    className="w-full"
                  />
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Straat + Huisnummer *</label>
                  <Input
                    type="text"
                    name="street_address"
                    value={formData.street_address}
                    onChange={handleInputChange}
                    required
                    placeholder="Leupegem 123"
                    className="w-full"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Postcode *</label>
                    <Input
                      type="text"
                      name="postal_code"
                      value={formData.postal_code}
                      onChange={handleInputChange}
                      required
                      placeholder="9700"
                      className="w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Gemeente *</label>
                    <Input
                      type="text"
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                      required
                      placeholder="Oudenaarde"
                      className="w-full"
                    />
                  </div>
                </div>
              </div>

              {/* Step 2: Number of People */}
              <div className="bg-green-50 p-6 rounded-xl">
                <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <span className="bg-green-600 text-white w-8 h-8 rounded-full flex items-center justify-center text-sm">2</span>
                  Aantal personen
                </h3>

                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Aantal volwassenen *</label>
                    <select
                      name="num_adults"
                      value={formData.num_adults}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600"
                      required
                    >
                      {[1, 2, 3, 4, 5, 6].map(num => (
                        <option key={num} value={num}>{num}</option>
                      ))}
                      <option value="7">6+</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Aantal kinderen</label>
                    <select
                      name="num_children"
                      value={formData.num_children}
                      onChange={handleNumChildrenChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600"
                    >
                      {[0, 1, 2, 3, 4, 5].map(num => (
                        <option key={num} value={num}>{num}</option>
                      ))}
                      <option value="6">5+</option>
                    </select>
                  </div>
                </div>

                {/* Children Ages */}
                {formData.num_children > 0 && (
                  <div className="bg-white p-4 rounded-lg border-2 border-green-200">
                    <p className="text-sm font-medium text-gray-700 mb-3">📝 Leeftijden van de kinderen *</p>
                    <div className="space-y-2">
                      {Array.from({ length: formData.num_children }, (_, index) => (
                        <div key={index} className="flex items-center gap-3">
                          <span className="text-sm text-gray-600 w-20">Kind {index + 1}:</span>
                          <select
                            value={formData.children_ages[index] || ''}
                            onChange={(e) => handleChildAgeChange(index, e.target.value)}
                            required
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600"
                          >
                            {AGE_OPTIONS.map(opt => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Step 3: Dietary Requirements */}
              <div className="bg-purple-50 p-6 rounded-xl">
                <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <span className="bg-purple-600 text-white w-8 h-8 rounded-full flex items-center justify-center text-sm">3</span>
                  Dieetwensen & Opmerkingen
                </h3>
                
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Dieetwensen of allergieën (optioneel)</label>
                  <Textarea
                    name="dietary_requirements"
                    value={formData.dietary_requirements}
                    onChange={handleInputChange}
                    rows={3}
                    placeholder="Bijvoorbeeld: vegetarisch, glutenvrij, notenallergie..."
                    className="w-full"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Extra opmerkingen (optioneel)</label>
                  <Textarea
                    name="comments"
                    value={formData.comments}
                    onChange={handleInputChange}
                    rows={2}
                    placeholder="Andere opmerkingen of vragen..."
                    className="w-full"
                  />
                </div>
              </div>

              {/* Privacy & Submit */}
              <div className="pt-4">
                <label className="flex items-start gap-3 mb-6 cursor-pointer">
                  <input
                    type="checkbox"
                    name="privacy_agreed"
                    checked={formData.privacy_agreed}
                    onChange={handleInputChange}
                    required
                    className="mt-1 w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">
                    Ik ga akkoord met de privacyverklaring en geef toestemming voor het gebruik van mijn gegevens voor deze nieuwjaarsreceptie. *
                  </span>
                </label>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-4 rounded-xl text-lg shadow-lg hover:scale-105 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                >
                  {isSubmitting ? 'Bezig met inschrijven...' : 'Inschrijving bevestigen'}
                </Button>

                <p className="text-xs text-gray-500 text-center mt-4">
                  Na verzending ontvangt u een bevestigingsmail
                </p>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-gray-400">
            © 2026 Feestcomité Leupegem. Alle rechten voorbehouden.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Nieuwjaarsreceptie;
